import { type WorkplaceProfileResponse } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useUpdatePreShiftOrientation } from "@src/appV2/FacilityProfile/api/useUpdatePreShiftOrientation";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

interface UseUpdatePreShiftOrientationSettingParams {
  workplace: WorkplaceProfileResponse | undefined;
  onSettingsChanged: () => void;
}

export interface PreShiftOrientationFormValues {
  orientationLengthInMinutes: number;
  qualifications: string[];
}

export function useUpdatePreShiftOrientationSetting({
  workplace,
  onSettingsChanged,
}: UseUpdatePreShiftOrientationSettingParams) {
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();

  const existingConfig = workplace?.data?.attributes?.preShiftOrientation;
  const workplaceId = workplace?.data?.id;

  const [enabled, setEnabled] = useState(existingConfig?.enabled ?? false);

  useEffect(() => {
    setEnabled(existingConfig?.enabled ?? false);
  }, [existingConfig?.enabled]);

  const editModalState = useModalState(ModalStatus.CLOSED);

  const { isLoading: isUpdating, mutateAsync: updatePreShiftOrientation } =
    useUpdatePreShiftOrientation();

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      editModalState.openModal();
      return;
    }

    if (!isDefined(workplaceId)) {
      showErrorToast(tu("Failed to update Start of Shift Orientation."));
      return;
    }

    setEnabled(false);
    try {
      await updatePreShiftOrientation({
        workplaceId,
        preShiftOrientation: { enabled: false },
      });
      showSuccessToast(tu("Start of Shift Orientation disabled successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update Start of Shift Orientation."));
      setEnabled(true);
    }
  };

  const handleSave = async (values: PreShiftOrientationFormValues) => {
    if (!isDefined(workplaceId)) {
      showErrorToast(tu("Failed to update Start of Shift Orientation."));
      return;
    }

    try {
      await updatePreShiftOrientation({
        workplaceId,
        preShiftOrientation: {
          enabled: true,
          orientationLengthInMinutes: values.orientationLengthInMinutes,
          qualifications: values.qualifications,
        },
      });
      setEnabled(true);
      editModalState.closeModal();
      showSuccessToast(tu("Start of Shift Orientation updated successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update Start of Shift Orientation."));
    }
  };

  return {
    editModalState,
    enabled,
    handleSave,
    handleToggle,
    isUpdating,
  };
}
